import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import ParceirosCompativeisCard from '@/components/parceiros/ParceirosCompativeisCard'
import EncaminhamentoParceiroCard from '@/components/parceiros/EncaminhamentoParceiroCard'
import { oportunidadeService } from '@/services/oportunidadeService'
import { ehSemPermissao } from '@/services/estruturacaoConsultorService'
import { authStore } from '@/store/authStore'
import { papelPrincipal } from '@/components/layout/menu'
import { Oportunidade } from '@/types/oportunidade'
import { formatCNPJ, formatCurrency, formatDate } from '@/utils/formatters'

/**
 * Seleção de Parceiro (Onda 6 / A11 — Figma "Seleção de Parceiro"): página própria da
 * reserva (`/reservas/:id/parceiro`) que REUSA os cards que já existem no detalhe —
 * `ParceirosCompativeisCard` (filtro com motivo, sem ranking: "a plataforma sugere e o
 * operador direciona") e `EncaminhamentoParceiroCard` (pacote formal ao parceiro).
 *
 * Quem vê: back-office. Quem seleciona/encaminha: ADMIN (espelha o `@PreAuthorize` de
 * `PUT /{id}/parceiro-selecionado` e do encaminhamento). O consultor passa a ver só o
 * parceiro selecionado, no detalhe da reserva.
 */
const SelecaoParceiroPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const papel = papelPrincipal(usuario?.permissoes)
  const backoffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'
  const podeSelecionar = papel === 'ADMIN'

  const [reserva, setReserva] = useState<Oportunidade | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)

  const carregar = useCallback(async () => {
    if (!id) return
    setCarregando(true)
    setErro(null)
    setSemPermissao(false)
    try {
      setReserva(await oportunidadeService.buscar(id))
    } catch (e) {
      if (ehSemPermissao(e)) setSemPermissao(true)
      else setErro((e as { message?: string } | null)?.message || 'Não foi possível carregar a reserva.')
    } finally {
      setCarregando(false)
    }
  }, [id])

  useEffect(() => {
    if (backoffice) void carregar()
  }, [backoffice, carregar])

  const selecionar = async (parceiroId: string, nome: string) => {
    if (!id || !usuario?.id) return
    try {
      const atualizada = await oportunidadeService.selecionarParceiro(id, {
        parceiroId,
        parceiroNome: nome,
        usuarioId: usuario.id,
      })
      setReserva((prev) => (prev ? { ...prev, ...atualizada } : atualizada))
      enqueueSnackbar(`Parceiro selecionado: ${nome}.`, { variant: 'success' })
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      enqueueSnackbar(msg || 'Não foi possível selecionar o parceiro.', { variant: 'error' })
    }
  }

  if (!backoffice) {
    return (
      <Box>
        <PageHeader title="Seleção de Parceiro" />
        <EstadoDeLista
          estado="semPermissao"
          detalhe="A seleção de parceiro é da Originei. O parceiro escolhido aparece no detalhe da sua reserva."
        />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Seleção de Parceiro"
        subtitle={
          reserva
            ? `${reserva.razaoSocial ?? reserva.nomeFantasia ?? 'Empresa'} · ${formatCNPJ(reserva.cnpj)} · ${formatCurrency(reserva.valorEstimado)}`
            : 'Parceiros compatíveis com a operação — a plataforma sugere, o operador direciona'
        }
        acao={
          reserva ? (
            <Button variant="outlined" onClick={() => navigate(`/oportunidades/${reserva.id}`)}>
              Abrir reserva
            </Button>
          ) : undefined
        }
      />

      {carregando ? (
        <EstadoDeLista estado="carregando" />
      ) : semPermissao ? (
        <EstadoDeLista estado="semPermissao" />
      ) : erro ? (
        <EstadoDeLista
          estado="erro"
          mensagem={erro}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : reserva ? (
        <Box>
          {reserva.parceiroSelecionadoNome && (
            <Card sx={{ mb: 3 }}>
              <CardHeader title="Parceiro da operação" />
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip color="primary" label={reserva.parceiroSelecionadoNome} />
                  {reserva.parceiroSelecionadoEm && (
                    <Typography variant="caption" color="text.secondary">
                      selecionado em {formatDate(reserva.parceiroSelecionadoEm)}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {!podeSelecionar && (
            <Alert severity="info" sx={{ mb: 2 }}>
              A seleção formal do parceiro é feita por ADMIN — aqui você vê as sugestões e o
              encaminhamento.
            </Alert>
          )}

          {reserva.produtoId ? (
            <ParceirosCompativeisCard
              produtoId={reserva.produtoId}
              valorEstimado={reserva.valorEstimado}
              parceiroSelecionadoId={reserva.parceiroSelecionadoId}
              onSelecionar={podeSelecionar ? selecionar : undefined}
            />
          ) : (
            <EstadoDeLista
              estado="vazio"
              mensagem="Reserva sem produto vinculado"
              detalhe="Sem o produto não há critérios para casar parceiros — vincule o produto na reserva antes de selecionar."
            />
          )}

          {id && reserva.parceiroSelecionadoId && (
            <Box sx={{ mt: 3 }}>
              <EncaminhamentoParceiroCard
                oportunidadeId={id}
                parceiroNome={reserva.parceiroSelecionadoNome}
                podeEncaminhar={podeSelecionar}
              />
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  )
}

export default SelecaoParceiroPage
